import { useState, useEffect } from 'react';
import { getNotificationAnalytics } from '../../services/notificationsService';
import BarChart from '../charts/BarChart';

const NotificationsTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [notificationData, setNotificationData] = useState<any>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await getNotificationAnalytics(30);
        setNotificationData(data);
      } catch (error) {
        console.error('Error loading notification data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading notification analytics...</p>
      </div>
    );
  }

  if (!notificationData) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>No notification data available</p>
      </div>
    );
  }

  // Notification engagement
  const engagementData = {
    labels: notificationData.notificationMetrics.map((n: any) => n.notificationType),
    datasets: [
      {
        label: 'Sent',
        data: notificationData.notificationMetrics.map((n: any) => n.sent),
        backgroundColor: '#2563eb',
      },
      {
        label: 'Delivered',
        data: notificationData.notificationMetrics.map((n: any) => n.delivered),
        backgroundColor: '#10b981',
      },
      {
        label: 'Opened',
        data: notificationData.notificationMetrics.map((n: any) => n.opened),
        backgroundColor: '#f59e0b',
      },
      {
        label: 'Clicked',
        data: notificationData.notificationMetrics.map((n: any) => n.clicked),
        backgroundColor: '#8b5cf6',
      },
    ],
  };

  // Campaign effectiveness
  const campaignData = {
    labels: notificationData.campaignMetrics.map((c: any) => c.campaignType),
    datasets: [
      {
        label: 'Engagement Rate (%)',
        data: notificationData.campaignMetrics.map((c: any) => c.engagementRate),
        backgroundColor: '#10b981',
      },
      {
        label: 'Conversion Rate (%)',
        data: notificationData.campaignMetrics.map((c: any) => c.conversionRate),
        backgroundColor: '#2563eb',
      },
    ],
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ marginBottom: '2rem', fontSize: '1.75rem', fontWeight: 'bold' }}>
        Notifications & Engagement Campaigns
      </h1>

      {/* KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Total Sent</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {notificationData.totalSent}
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Delivery Rate</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {notificationData.deliveryRate.toFixed(1)}%
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Open Rate</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {notificationData.openRate.toFixed(1)}%
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Click Rate</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {notificationData.clickRate.toFixed(1)}%
          </h2>
        </div>
      </div>

      {/* Charts */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Notification Engagement by Type</h3>
          <BarChart data={engagementData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Campaign Effectiveness</h3>
          <BarChart data={campaignData} height={300} />
        </div>
      </div>

      {/* Notification Metrics Table */}
      {notificationData.notificationMetrics.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}>
          <h3 style={{ padding: '1rem', margin: 0, borderBottom: '1px solid #e0e0e0' }}>
            Notification Metrics by Type
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Type</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Sent</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Delivered</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Opened</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Clicked</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Engagement Rate</th>
              </tr>
            </thead>
            <tbody>
              {notificationData.notificationMetrics.map((metric: any, index: number) => {
                const engagementRate = metric.delivered > 0 ? (metric.opened / metric.delivered) * 100 : 0;
                return (
                  <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '1rem' }}>{metric.notificationType}</td>
                    <td style={{ padding: '1rem' }}>{metric.sent}</td>
                    <td style={{ padding: '1rem' }}>{metric.delivered}</td>
                    <td style={{ padding: '1rem' }}>{metric.opened}</td>
                    <td style={{ padding: '1rem' }}>{metric.clicked}</td>
                    <td style={{ padding: '1rem', color: engagementRate > 20 ? '#10b981' : '#666' }}>
                      {engagementRate.toFixed(1)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default NotificationsTab;

